import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useId, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import './PersonBadge.css';

const TOOLTIP_MAX_W = 300;
const TOOLTIP_APPROX_H = 168;
const TOOLTIP_SPACING = 12;
const VIEW_MARGIN = 8;
const HOVER_CLOSE_MS = 220;

// PersonBadge: hover tooltip by default; iconClick uses an external info button (see renderInfoTrigger).
// Tooltip is portaled to document.body so it is not clipped by table / overflow containers.
const PersonBadge = forwardRef(function PersonBadge({
  name,
  empId,
  email,
  contact,
  icon,
  badgeType = 'default',
  isAssigned = false,
  role = null,
  triggerMode = 'hover',
  externalTriggerRef = null,
  onTooltipOpenChange = null,
}, ref) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [tooltipPlacement, setTooltipPlacement] = useState('left');
  const badgeRef = useRef(null);
  const tooltipRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  const touchAutoHideRef = useRef(null);
  const tooltipId = useId();

  const setTooltipVisible = useCallback((visible) => {
    setShowTooltip(visible);
    onTooltipOpenChange?.(visible);
  }, [onTooltipOpenChange]);

  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }, []);

  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    hoverCloseTimerRef.current = setTimeout(() => {
      setTooltipVisible(false);
      hoverCloseTimerRef.current = null;
    }, HOVER_CLOSE_MS);
  }, [cancelHoverClose, setTooltipVisible]);

  const calculateTooltipPosition = useCallback(() => {
    if (!badgeRef.current) return;

    const rect = badgeRef.current.getBoundingClientRect();
    const tw = TOOLTIP_MAX_W;
    const th = TOOLTIP_APPROX_H;
    const spacing = TOOLTIP_SPACING;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceOnLeft = rect.left - VIEW_MARGIN;
    const canShowOnLeft = spaceOnLeft >= tw + spacing;

    if (canShowOnLeft) {
      const badgeMidY = rect.top + rect.height / 2;
      let top = badgeMidY - th / 2;
      top = Math.max(VIEW_MARGIN, Math.min(top, vh - th - VIEW_MARGIN));
      setTooltipPlacement('left');
      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${rect.left - tw - spacing}px`,
        transform: 'none',
        zIndex: 10050
      });
      return;
    }

    let placement = 'above';
    let top = rect.top - th - spacing;
    if (top < VIEW_MARGIN && rect.bottom + th + spacing <= vh - VIEW_MARGIN) {
      placement = 'below';
      top = rect.bottom + spacing;
    }
    top = Math.max(VIEW_MARGIN, Math.min(top, vh - th - VIEW_MARGIN));

    let centerX = rect.left + rect.width / 2;
    const halfMin = tw / 2 + VIEW_MARGIN;
    centerX = Math.max(halfMin, Math.min(centerX, vw - halfMin));

    setTooltipPlacement(placement);
    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${centerX}px`,
      transform: 'translateX(-50%)',
      zIndex: 10050
    });
  }, []);

  const openTooltip = useCallback(() => {
    cancelHoverClose();
    calculateTooltipPosition();
    setTooltipVisible(true);
  }, [calculateTooltipPosition, cancelHoverClose, setTooltipVisible]);

  const toggleTooltip = useCallback(() => {
    if (showTooltip) {
      cancelHoverClose();
      setTooltipVisible(false);
    } else {
      openTooltip();
    }
  }, [showTooltip, cancelHoverClose, setTooltipVisible, openTooltip]);

  useImperativeHandle(ref, () => ({
    toggleTooltip,
    openTooltip,
    closeTooltip: () => setTooltipVisible(false),
  }), [toggleTooltip, openTooltip, setTooltipVisible]);

  useLayoutEffect(() => {
    if (!showTooltip) return;
    calculateTooltipPosition();
  }, [showTooltip, calculateTooltipPosition]);

  useEffect(() => {
    if (!showTooltip) return;
    const onScrollOrResize = () => calculateTooltipPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showTooltip, calculateTooltipPosition]);

  const useHoverTrigger = triggerMode === 'hover';
  const useIconClickTrigger = triggerMode === 'iconClick';

  const handleMouseEnter = () => {
    if (!useHoverTrigger) return;
    openTooltip();
  };

  const handleMouseLeave = () => {
    if (!useHoverTrigger) return;
    scheduleHoverClose();
  };

  const handleTouchStart = () => {
    if (!useHoverTrigger) return;
    if (touchAutoHideRef.current) {
      clearTimeout(touchAutoHideRef.current);
      touchAutoHideRef.current = null;
    }
    calculateTooltipPosition();
    setTooltipVisible(true);
  };

  const handleTouchEnd = () => {
    if (!useHoverTrigger) return;
    if (touchAutoHideRef.current) clearTimeout(touchAutoHideRef.current);
    touchAutoHideRef.current = setTimeout(() => {
      setTooltipVisible(false);
      touchAutoHideRef.current = null;
    }, 5000);
  };

  const handleCloseTooltip = (e) => {
    e.stopPropagation();
    setTooltipVisible(false);
  };

  useEffect(() => {
    if (!showTooltip) return undefined;
    const onDocClick = (e) => {
      if (badgeRef.current?.contains(e.target)) return;
      if (externalTriggerRef?.current?.contains(e.target)) return;
      if (tooltipRef.current?.contains(e.target)) return;
      setTooltipVisible(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setTooltipVisible(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showTooltip, externalTriggerRef, setTooltipVisible]);

  useEffect(() => () => {
    cancelHoverClose();
    if (touchAutoHideRef.current) {
      clearTimeout(touchAutoHideRef.current);
    }
  }, [cancelHoverClose]);

  const tooltipContent = showTooltip && isAssigned && (
    <div
      ref={tooltipRef}
      id={tooltipId}
      className="person-tooltip person-tooltip-portal"
      data-placement={tooltipPlacement}
      style={tooltipStyle}
      role="tooltip"
      aria-label={`Information for ${name}`}
      onMouseEnter={useHoverTrigger ? cancelHoverClose : undefined}
      onMouseLeave={useHoverTrigger ? scheduleHoverClose : undefined}
    >
      <div className="tooltip-content">
        <div className="tooltip-row">
          <span className="tooltip-label">Name:</span>
          <span className="tooltip-value">{name}</span>
        </div>
        {role && (
          <div className="tooltip-row">
            <span className="tooltip-label">Role:</span>
            <span className="tooltip-value tooltip-role">{role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
          </div>
        )}
        {empId && (
          <div className="tooltip-row">
            <span className="tooltip-label">Employee ID:</span>
            <span className="tooltip-value tooltip-mono">{empId}</span>
          </div>
        )}
        {email && email !== 'N/A' && (
          <div className="tooltip-row">
            <span className="tooltip-label">Email:</span>
            <span className="tooltip-value">{email}</span>
          </div>
        )}
        {contact && contact !== 'N/A' && (
          <div className="tooltip-row">
            <span className="tooltip-label">Contact:</span>
            <span className="tooltip-value">{contact}</span>
          </div>
        )}
      </div>
      <button
        type="button"
        className="tooltip-close-btn"
        onClick={handleCloseTooltip}
        aria-label="Close tooltip"
        title="Close (or press Escape)"
      >
        ✕
      </button>
    </div>
  );

  if (!isAssigned && badgeType === 'unassigned') {
    return (
      <div className="person-badge unassigned-badge">
        <span className="person-icon">{icon}</span>
        <span className="person-name unassigned-text">{name}</span>
      </div>
    );
  }

  return (
    <>
      <div
        ref={badgeRef}
        className={`person-badge ${badgeType === 'assigned' ? 'assigned-badge' : ''} ${useIconClickTrigger ? 'person-badge--static' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onKeyDown={useHoverTrigger ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openTooltip();
          }
        } : undefined}
        tabIndex={useHoverTrigger ? 0 : undefined}
        role={useHoverTrigger ? 'button' : undefined}
        aria-pressed={useHoverTrigger ? showTooltip : undefined}
        aria-describedby={showTooltip ? tooltipId : undefined}
      >
        <span className="person-icon">{icon}</span>
        <span className="person-name">{name}</span>
      </div>
      {tooltipContent && ReactDOM.createPortal(tooltipContent, document.body)}
    </>
  );
});

export function PersonBadgeInfoButton({ buttonRef, onClick, ariaLabel, expanded }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className="person-info-btn"
      onClick={onClick}
      onMouseDown={(e) => e.stopPropagation()}
      aria-expanded={expanded}
      aria-label={ariaLabel}
    >
      <span className="person-info-icon" aria-hidden="true">ℹ️</span>
    </button>
  );
}

export default PersonBadge;
